import { Dispatch, useCallback, useEffect, useMemo, useRef } from 'react';
import { ObjectType } from '../../../Types/base';
import { User } from '../../../Types/user';
import {
	WhiteboardFile,
	WhiteboardPresenceMessage,
	WhiteboardSignalREvent,
	WhiteboardSignalREventType,
	WhiteboardYjsUpdate,
} from '../../../Types/whiteboard';
import { HubConnectionState, useSignalRContext } from '../../../Contexts/signalrContext';
import { PRESENCE_THROTTLE_MS } from '../constants';
import { WhiteboardAction } from '../reducer';

export const useWhiteboardSignalR = (
	objectType: ObjectType,
	objectId: number,
	user: User | undefined,
	dispatch: Dispatch<WhiteboardAction>,
	addFiles: (files: WhiteboardFile[]) => Promise<void>,
	applyYjsUpdate: (update: WhiteboardYjsUpdate) => void,
	resetQueryJoinWhiteboard: () => void
) => {
	const presenceThrottleRef = useRef<NodeJS.Timeout | null>(null);
	const { subscribe, unsubscribe, sendMessage, connectionId } = useSignalRContext();

	// Store functions in refs to avoid dependency issues
	const addFilesRef = useRef(addFiles);
	const applyYjsUpdateRef = useRef(applyYjsUpdate);
	const resetQueryJoinWhiteboardRef = useRef(resetQueryJoinWhiteboard);

	// Update refs when functions change
	useEffect(() => {
		addFilesRef.current = addFiles;
	}, [addFiles]);

	useEffect(() => {
		applyYjsUpdateRef.current = applyYjsUpdate;
	}, [applyYjsUpdate]);

	useEffect(() => {
		resetQueryJoinWhiteboardRef.current = resetQueryJoinWhiteboard;
	}, [resetQueryJoinWhiteboard]);

	// Presence update sender
	const sendPresenceUpdate = useCallback(
		(x: number, y: number, lastPresenceUpdate: number) => {
			const now = Date.now();
			if (now - lastPresenceUpdate < PRESENCE_THROTTLE_MS) return;

			dispatch({ type: 'SET_PRESENCE_UPDATE_TIME', payload: now });

			if (presenceThrottleRef.current) {
				clearTimeout(presenceThrottleRef.current);
			}

			presenceThrottleRef.current = setTimeout(() => {
				const message: WhiteboardPresenceMessage = {
					t: 'cursor',
					x,
					y,
					userId: user?.Id || -1,
					userName: user?.FullName || 'Unknown',
				};

				sendMessage('WHITEBOARD', {
					EventType: WhiteboardSignalREventType.Presence,
					ObjectType: objectType,
					ObjectId: objectId,
					Content: message,
				} satisfies WhiteboardSignalREvent);
			}, 10);
		},
		[objectType, objectId, sendMessage, user?.Id, user?.FullName, dispatch]
	);

	// Handle SignalR events
	useEffect(() => {
		if (objectType <= 0 || objectId <= 0) return;

		console.log('Setting up Whiteboard SignalR subscription');

		// Handler functions for different SignalR event types
		const handleYjsUpdate = (content: unknown, originConnectionId?: string) => {
			try {
				if (originConnectionId === connectionId) {
					console.log('Ignoring remote update from same connection', { originConnectionId, connectionId });
					return;
				}
				const update = content as WhiteboardYjsUpdate;
				applyYjsUpdateRef.current(update);
			} catch (error) {
				console.error('Error processing remote update', error);
			}
		};

		const handlePresenceUpdate = (content: unknown) => {
			try {
				const message = content as WhiteboardPresenceMessage;
				if (message && message.t === 'cursor' && message.userId !== user?.Id) {
					dispatch({
						type: 'UPDATE_CURSOR',
						payload: {
							userId: message.userId,
							x: message.x,
							y: message.y,
							userName: message.userName,
						},
					});
				}
			} catch (error) {
				console.error('Error processing presence update:', error);
			}
		};

		const handleFilesUpdated = (content: unknown) => {
			try {
				console.log('Received an update of the files list via SignalR', content);
				const files = content as WhiteboardFile[];
				addFilesRef.current(files);
			} catch (error) {
				console.error('Error processing files', error);
			}
		};

		// Main SignalR event handler
		const handleWhiteboardUpdate = (content: unknown) => {
			const event = content as WhiteboardSignalREvent;
			if (event.ObjectType !== objectType || event.ObjectId !== objectId) {
				console.log('Ignoring event for different whiteboard', { event, objectType, objectId });
				return;
			}

			switch (event.EventType) {
				case WhiteboardSignalREventType.JoinConfirmed:
					console.log('SignalR join confirmed, now fetching latest whiteboard snapshot');
					dispatch({ type: 'SET_JOINED_SIGNALR', payload: true });
					break;
				case WhiteboardSignalREventType.VJS_Update:
					handleYjsUpdate(event.Content, event.OriginConnectionId);
					break;
				case WhiteboardSignalREventType.FilesUpdated:
					handleFilesUpdated(event.Content);
					break;
				case WhiteboardSignalREventType.Presence:
					handlePresenceUpdate(event.Content);
					break;
				default:
					console.log('Unknown whiteboard event type received:', event);
					break;
			}
		};

		const joinGroup = () => {
			console.log('Joining SignalR group for whiteboard', { objectType, objectId });
			sendMessage('WHITEBOARD', {
				EventType: WhiteboardSignalREventType.JoinGroup,
				ObjectType: objectType,
				ObjectId: objectId,
			} satisfies WhiteboardSignalREvent);
		};

		// Connection state change handler
		const handleConnectionStateChangeEvent = (newState: HubConnectionState) => {
			if (newState === 'CONNECTED') {
				console.log('SignalR connection state change detected as CONNECTED');
				joinGroup();
			} else {
				console.log('SignalR connection state change detected, no longer connected', { newConnectionState: newState });
				resetQueryJoinWhiteboardRef.current();
				dispatch({ type: 'SET_JOINED_SIGNALR', payload: false });
				dispatch({ type: 'SET_LOADING', payload: true });
			}
		};

		// Initialize SignalR subscription
		const initSubscription = () => {
			subscribe('HubGeneric', 'WHITEBOARD', null, handleWhiteboardUpdate, handleConnectionStateChangeEvent);

			setTimeout(() => {
				joinGroup();
			}, 500);
		};

		initSubscription();

		// Cleanup on unmount
		return () => {
			sendMessage('WHITEBOARD', {
				EventType: WhiteboardSignalREventType.LeaveGroup,
				ObjectType: objectType,
				ObjectId: objectId,
			} satisfies WhiteboardSignalREvent);
			unsubscribe('HubGeneric', 'WHITEBOARD', null);
			dispatch({ type: 'SET_JOINED_SIGNALR', payload: false });
			if (presenceThrottleRef.current) {
				clearTimeout(presenceThrottleRef.current);
			}
		};
	}, [objectType, objectId, sendMessage, subscribe, unsubscribe, user?.Id, dispatch]);

	return useMemo(
		() => ({
			sendPresenceUpdate,
		}),
		[sendPresenceUpdate]
	);
};
